import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

export type ResolveActionKey = "updateVendorData" | "resolveExternally" | "nonDiscrepancy";

export interface ResolveActionOption {
  action: ResolveActionKey;
  label: string;
  /** Plain-language explanation of what the action does, shown under its radio. */
  description: string;
  /** The subset of targeted rows this action can apply to. */
  eligibleIds: string[];
  /** Bulleted reasons rows are skipped (from formatSkipSummary), when any are. */
  skipSummary?: string;
}

export interface ResolveDiscrepanciesDialogProps {
  open: boolean;
  /** How many rows the dialog is acting on — the selection size, or 1 for a row action. */
  totalCount: number;
  options: ResolveActionOption[];
  loading: boolean;
  onClose: () => void;
  onConfirm: (action: ResolveActionKey, eligibleIds: string[], note: string | null) => void;
}

/**
 * The single resolution flow for discrepancy rows: pick an action (each
 * explained inline, with per-action eligibility for the targeted rows), add an
 * optional note, confirm. Opened from a section's "Resolve Selected" bulk
 * button or a row's resolve action; the caller decides which actions are
 * offered (Data Quality Issues omit "Correct Vendor Data").
 */
export function ResolveDiscrepanciesDialog({
  open,
  totalCount,
  options,
  loading,
  onClose,
  onConfirm,
}: ResolveDiscrepanciesDialogProps) {
  const [selectedAction, setSelectedAction] = useState<ResolveActionKey | null>(null);
  const [note, setNote] = useState("");

  // Fresh state per open — the dialog stays mounted between uses.
  useEffect(() => {
    if (!open) {
      setSelectedAction(null);
      setNote("");
    }
  }, [open]);

  const selectedOption = options.find((o) => o.action === selectedAction);
  const rowWord = totalCount === 1 ? "row" : "rows";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {totalCount === 1 ? "Resolve Discrepancy" : `Resolve ${totalCount} Selected Rows`}
      </DialogTitle>
      <DialogContent>
        <RadioGroup
          value={selectedAction ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "updateVendorData" || v === "resolveExternally" || v === "nonDiscrepancy") {
              setSelectedAction(v);
            }
          }}
        >
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {options.map((option) => {
              const eligibleCount = option.eligibleIds.length;
              const noneEligible = eligibleCount === 0;
              const partial = eligibleCount > 0 && eligibleCount < totalCount;
              return (
                <FormControlLabel
                  key={option.action}
                  value={option.action}
                  disabled={noneEligible || loading}
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={<Radio size="small" sx={{ mt: -0.5 }} />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                      {noneEligible && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {`Not available for the selected ${rowWord}${
                            option.skipSummary ? `:\n${option.skipSummary}` : "."
                          }`}
                        </Typography>
                      )}
                      {partial && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {`Applies to ${eligibleCount} of ${totalCount} selected rows. Skipped:\n${
                            option.skipSummary ?? ""
                          }`}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        </RadioGroup>
        <TextField
          label="Note (optional)"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          sx={{ mt: 2.5 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          helperText="Saved with the resolution, whichever action you choose."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!selectedOption || selectedOption.eligibleIds.length === 0 || loading}
          onClick={() => {
            if (!selectedOption) return;
            onConfirm(selectedOption.action, selectedOption.eligibleIds, note || null);
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
